'use client';

import { type ReactNode, useCallback, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';

import { AnimatePresence } from 'motion/react';

import { type OpenPostDetailInput, PostDetailModalContext } from './context';

/**
 * Lazy-loaded: the detail modal (PostView + comments + media carousel) only
 * mounts after the first `openPostDetail(...)`, so it stays out of the first-load
 * bundle of every surface that mounts this provider (DM inbox, community
 * channels). `ssr: false` — it never renders on first paint (gated behind state
 * set only by a client interaction).
 */
const PostDetailModal = dynamic(
  () => import('./PostDetailModal').then(mod => mod.PostDetailModal),
  { ssr: false },
);

/**
 * Mounts the single, surface-wide post-detail modal and exposes
 * `openPostDetail(...)` to every descendant. Drop once per surface that shows
 * post chat cards (the dashboard shell for DM + owner community, the bio
 * community layout for visitors) — BELOW `AuthProvider`, since the modal's own
 * `UserProfileProvider`/`BioUserAccessProvider` need a signed-in user. One
 * shared mount, many open calls (mirrors `ShareDialogProvider`) — so a chat card
 * never renders its own modal (and can't lose it when a recycling list unmounts
 * the row).
 */
export function PostDetailModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OpenPostDetailInput | null>(null);

  const openPostDetail = useCallback((input: OpenPostDetailInput) => {
    setState(input);
  }, []);
  const closePostDetail = useCallback(() => setState(null), []);

  const value = useMemo(
    () => ({ openPostDetail, closePostDetail }),
    [openPostDetail, closePostDetail],
  );

  return (
    <PostDetailModalContext.Provider value={value}>
      {children}
      {/* AnimatePresence keeps the modal mounted through its dismiss (exit)
          transition after `state` clears, so it animates out instead of
          vanishing. */}
      <AnimatePresence>
        {state ? (
          <PostDetailModal
            key="post-detail-modal"
            storeId={state.storeId}
            postId={state.postId}
            urlCode={state.urlCode}
            bioContext={state.bioContext}
            onClose={closePostDetail}
          />
        ) : null}
      </AnimatePresence>
    </PostDetailModalContext.Provider>
  );
}
